// Firestore Metrics Management
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  increment, 
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Campaign configurations
export const CAMPAIGNS = {
  'free-inspect-weed': {
    title: 'Free Inspection and Weed Pulling',
    source: 'door-to-door',
    medium: 'hangtag',
    campaign: 'free-inspect-weed',
    term: 'october-2025'
  },
  'flyer-1': {
    title: 'Junk Removal, Tree Trimming, Power Washing & Gutter Cleaning Special',
    source: 'flyer',
    medium: 'specific-date-junk-tree-trimming-power-washing-gutter-cleaning',
    campaign: 'flyer-1',
    term: 'october'
  },
  '25-off-6-months': {
    title: '25% Off 6 month plan',
    source: 'door-to-door',
    medium: 'hangtag',
    campaign: '25-off-6-months',
    term: 'october-2025'
  },
  '30-off-3-months': {
    title: '30% Off 3 month plan',
    source: 'door-to-door',
    medium: 'hangtag',
    campaign: '30-off-3-months',
    term: 'october-2025'
  }
};

// Track campaign landing
export const trackCampaignLanding = async (campaignKey) => {
  try {
    if (!campaignKey) {
      console.error('Invalid campaign key:', campaignKey);
      return;
    }

    // Check if we've already tracked this campaign in this session
    const sessionKey = `firestore_tracked_${campaignKey}`;
    if (sessionStorage.getItem(sessionKey)) {
      console.log('Campaign already tracked in Firestore for this session:', campaignKey);
      return;
    }

    const campaignRef = doc(db, 'campaign_metrics', campaignKey);
    const campaignSnap = await getDoc(campaignRef);

    if (campaignSnap.exists()) {
      // Update existing campaign metrics
      await updateDoc(campaignRef, {
        visits: increment(1),
        lastVisit: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new campaign metrics - use static data if available, otherwise create minimal data
      const campaignData = CAMPAIGNS[campaignKey] || {
        title: campaignKey.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        source: 'unknown',
        medium: 'unknown',
        campaign: campaignKey,
        term: '',
        content: ''
      };
      
      await setDoc(campaignRef, {
        ...campaignData,
        visits: 1,
        quotes: 0,
        revenue: 0,
        conversionRate: 0,
        createdAt: serverTimestamp(),
        lastVisit: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    // Mark as tracked in this session
    sessionStorage.setItem(sessionKey, 'true');

    console.log('Campaign landing tracked:', campaignKey);
  } catch (error) {
    console.error('Error tracking campaign landing:', error);
  }
};

// Track campaign quote submission
export const trackCampaignQuote = async (campaignKey, quoteData) => {
  try {
    if (!campaignKey) {
      console.error('Invalid campaign key:', campaignKey);
      return;
    }

    const campaignRef = doc(db, 'campaign_metrics', campaignKey);
    const campaignSnap = await getDoc(campaignRef);

    if (campaignSnap.exists()) {
      const currentData = campaignSnap.data();
      const newQuotes = currentData.quotes + 1;
      const newRevenue = currentData.revenue + (quoteData.quoteAmount || 500);
      const newConversionRate = currentData.visits > 0 ? 
        parseFloat(((newQuotes / currentData.visits) * 100).toFixed(2)) : 0;

      await updateDoc(campaignRef, {
        quotes: increment(1),
        revenue: increment(quoteData.quoteAmount || 500),
        conversionRate: newConversionRate,
        lastQuote: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new campaign metrics if it doesn't exist
      const campaignData = CAMPAIGNS[campaignKey] || {
        title: campaignKey.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        source: 'unknown',
        medium: 'unknown',
        campaign: campaignKey,
        term: '',
        content: ''
      };
      
      await setDoc(campaignRef, {
        ...campaignData,
        visits: 1, // Assume at least 1 visit if quote is submitted
        quotes: 1,
        revenue: quoteData.quoteAmount || 500,
        conversionRate: 100, // 100% if first quote
        createdAt: serverTimestamp(),
        lastQuote: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    console.log('Campaign quote tracked:', campaignKey, quoteData);
  } catch (error) {
    console.error('Error tracking campaign quote:', error);
  }
};

// Get all campaign metrics
export const getCampaignMetrics = async () => {
  try {
    const campaigns = {};
    
    // Get all campaigns from Firestore
    const campaignsQuery = query(collection(db, 'campaign_metrics'));
    const campaignsSnapshot = await getDocs(campaignsQuery);
    
    campaignsSnapshot.forEach((doc) => {
      const data = doc.data();
      campaigns[doc.id] = {
        ...data,
        id: doc.id
      };
    });

    // Also include any campaigns from the static CAMPAIGNS object that might not be in Firestore yet
    for (const [campaignKey, campaignData] of Object.entries(CAMPAIGNS)) {
      if (!campaigns[campaignKey]) {
        campaigns[campaignKey] = {
          ...campaignData,
          id: campaignKey,
          visits: 0,
          quotes: 0,
          revenue: 0,
          conversionRate: 0
        };
      }
    }

    return campaigns;
  } catch (error) {
    console.error('Error getting campaign metrics:', error);
    return {};
  }
};

// Get overall analytics metrics
export const getAnalyticsMetrics = async (dateRange = null) => {
  try {
    // Get quotes data
    const quotesQuery = query(
      collection(db, 'quotes'),
      orderBy('createdAt', 'desc'),
      limit(1000)
    );

    const quotesSnapshot = await getDocs(quotesQuery);
    const quotes = [];
    quotesSnapshot.forEach((doc) => {
      quotes.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      });
    });

    // Filter by date range if provided
    let filteredQuotes = quotes;
    if (dateRange && dateRange.length === 2) {
      const startDate = new Date(dateRange[0]);
      const endDate = new Date(dateRange[1]);
      filteredQuotes = quotes.filter(quote => 
        quote.createdAt >= startDate && quote.createdAt <= endDate
      );
    }

    // Calculate metrics
    const totalQuotes = filteredQuotes.length;
    const uniqueEmails = new Set(filteredQuotes.map(quote => quote.email)).size;
    const conversionRate = uniqueEmails > 0 ? (totalQuotes / uniqueEmails) * 100 : 0;

    // Get campaign metrics
    const campaignData = await getCampaignMetrics();

    // Calculate total page views (estimate based on quotes and campaign visits)
    const totalVisits = Object.values(campaignData).reduce((sum, campaign) => sum + (campaign.visits || 0), 0);
    const totalPageViews = Math.max(totalVisits, totalQuotes * 5); // At least 5x quotes

    return {
      success: true,
      data: {
        totalPageViews,
        uniqueUsers: uniqueEmails,
        quotesSubmitted: totalQuotes,
        conversionRate: parseFloat(conversionRate.toFixed(2)),
        campaignData,
        recentQuotes: filteredQuotes.slice(0, 10).map(quote => ({
          id: quote.id,
          email: quote.email,
          address: quote.address,
          services: Object.keys(quote.selectedServices || {}).filter(key => quote.selectedServices[key]),
          createdAt: quote.createdAt,
          amount: quote.quoteAmount || 0,
          campaign: quote.utmCampaign?.campaign || 'direct'
        }))
      }
    };

  } catch (error) {
    console.error('Error getting analytics metrics:', error);
    return {
      success: false,
      error: error.message,
      data: getFallbackData()
    };
  }
};

// Create a new campaign
export const createCampaign = async (campaignData) => {
  try {
    const { title, source, medium, campaign, term, content } = campaignData;
    
    // Validate required fields
    if (!title || !source || !medium || !campaign) {
      throw new Error('Title, source, medium, and campaign are required fields');
    }

    // Create campaign key from campaign name (lowercase, replace spaces with hyphens)
    const campaignKey = campaign.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    const campaignRef = doc(db, 'campaign_metrics', campaignKey);
    const campaignSnap = await getDoc(campaignRef);

    if (campaignSnap.exists()) {
      throw new Error('Campaign with this key already exists');
    }

    // Create new campaign
    await setDoc(campaignRef, {
      title,
      source,
      medium,
      campaign: campaignKey,
      term: term || '',
      content: content || '',
      visits: 0,
      quotes: 0,
      revenue: 0,
      conversionRate: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    console.log('Campaign created successfully:', campaignKey);
    return { success: true, campaignKey };
  } catch (error) {
    console.error('Error creating campaign:', error);
    return { success: false, error: error.message };
  }
};

// Update an existing campaign
export const updateCampaign = async (campaignKey, campaignData) => {
  try {
    const campaignRef = doc(db, 'campaign_metrics', campaignKey);
    const campaignSnap = await getDoc(campaignRef);

    if (!campaignSnap.exists()) {
      throw new Error('Campaign not found');
    }

    await updateDoc(campaignRef, {
      ...campaignData,
      updatedAt: serverTimestamp()
    });

    console.log('Campaign updated successfully:', campaignKey);
    return { success: true };
  } catch (error) {
    console.error('Error updating campaign:', error);
    return { success: false, error: error.message };
  }
};

// Delete a campaign
export const deleteCampaign = async (campaignKey) => {
  try {
    const campaignRef = doc(db, 'campaign_metrics', campaignKey);
    const campaignSnap = await getDoc(campaignRef);

    if (!campaignSnap.exists()) {
      throw new Error('Campaign not found');
    }

    await deleteDoc(campaignRef);
    console.log('Campaign deleted successfully:', campaignKey);
    return { success: true };
  } catch (error) {
    console.error('Error deleting campaign:', error);
    return { success: false, error: error.message };
  }
};

// Generate campaign URL
export const generateCampaignUrl = (campaignData) => {
  const { source, medium, campaign, term, content } = campaignData;
  const baseUrl = window.location.origin;
  const params = new URLSearchParams();
  
  if (source) params.append('utm_source', source);
  if (medium) params.append('utm_medium', medium);
  if (campaign) params.append('utm_campaign', campaign);
  if (term) params.append('utm_term', term);
  if (content) params.append('utm_content', content);
  
  return `${baseUrl}?${params.toString()}`;
};

// Fallback data when Firebase is not available
const getFallbackData = () => {
  return {
    totalPageViews: 0,
    uniqueUsers: 0,
    quotesSubmitted: 0,
    conversionRate: 0,
    campaignData: {
      'free-inspect-weed': { title: 'Free Inspection and Weed Pulling', visits: 0, quotes: 0, conversionRate: 0, revenue: 0, campaign: 'free-inspect-weed' },
      'flyer-1': { title: 'Junk Removal, Tree Trimming, Power Washing & Gutter Cleaning Special', visits: 0, quotes: 0, conversionRate: 0, revenue: 0, campaign: 'flyer-1' },
      '25-off-6-months': { title: '25% Off 6 month plan', visits: 0, quotes: 0, conversionRate: 0, revenue: 0, campaign: '25-off-6-months' },
      '30-off-3-months': { title: '30% Off 3 month plan', visits: 0, quotes: 0, conversionRate: 0, revenue: 0, campaign: '30-off-3-months' }
    },
    recentQuotes: []
  };
};
